import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Construction, Truck, Anchor, Container, PackageOpen, Warehouse,
  ArrowRight, ArrowUpRight, ShieldCheck,
} from "lucide-react";
import { COMPANY, SERVICES, CERTIFICATIONS } from "@/data/site";
import { MaskedLines, Reveal, MaskedLinesInView, FadeUp } from "@/components/Reveal";
import { Placeholder } from "@/components/Placeholder";
import { Marquee } from "@/components/Marquee";
import { CTABand } from "@/components/CTABand";
import { usePageMeta } from "@/components/PageHero";

const ICONS = {
  "crane-rental": Construction,
  "boom-trucks": Truck,
  "rigging-hauling": Anchor,
  "truck-rental": Container,
  "delivery-offloading": PackageOpen,
  storage: Warehouse,
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden bg-[#0F1D45]" data-testid="home-hero">
      {/* Photo background with slow parallax drift */}
      <motion.div style={{ y }} className="absolute -inset-y-16 inset-x-0">
        <img
          src={COMPANY.heroImage}
          alt={COMPANY.heroImageAlt}
          fetchpriority="high"
          decoding="async"
          className="w-full h-full object-cover object-[70%_center]"
        />
      </motion.div>
      {/* Navy gradient: strong on the left for text legibility, light on the right so the crane reads */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F1D45]/90 via-[#0F1D45]/60 to-[#0F1D45]/15" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1D45]/80 via-transparent to-[#0F1D45]/30" />

      <motion.div style={{ opacity }} className="relative max-w-[1440px] mx-auto w-full px-6 md:px-12 pb-24 md:pb-28 pt-40">
        <FadeUp as="p" delay={0.1} className="overline text-white/80 mb-8 flex items-center gap-3">
          <span className="w-10 h-px bg-white/70" /> Since {COMPANY.since} · Richmond, VA
        </FadeUp>

        <h1 className="text-[14vw] sm:text-[12vw] md:text-[9.5vw] lg:text-[8vw] xl:text-[7.5vw] leading-[0.9] uppercase">
          <MaskedLines lines={["We Lift Richmond"]} start={0.2} lineClassName="text-white" />
          <MaskedLines lines={["To New Heights"]} start={0.35} lineClassName="text-[#9FB4E8]" />
        </h1>

        <FadeUp delay={0.9} className="mt-10 grid gap-8 md:grid-cols-[1fr_auto] md:items-end">
          <p className="max-w-xl text-white/80 text-base md:text-lg leading-relaxed">
            Crane and truck rental, rigging, and hauling. Done right, every time, for over 100 years.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="hero-book-btn" className="group inline-flex items-center justify-center gap-3 bg-white text-[#1C3172] px-8 py-5 font-mono-plex uppercase tracking-widest text-sm transition-colors duration-300 hover:bg-[#E8EDF8]">
              Book a Crane or Truck
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/services" data-testid="hero-services-btn" className="group inline-flex items-center justify-center gap-3 border border-white/50 text-white px-8 py-5 font-mono-plex uppercase tracking-widest text-sm transition-colors duration-300 hover:bg-white hover:text-[#1C3172]">
              See Our Services
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </FadeUp>
      </motion.div>

      <div className="relative border-y border-[#E5E7EB] bg-[#F7F8FA]">
        <Marquee
          className="py-4 font-mono-plex uppercase tracking-[0.2em] text-sm text-[#1A1A1A]/70"
          items={["30-Ton to 160-Ton Cranes", "CCO Certified Operators", "Rigging & Hauling", "24/7 Dispatch", "Serving Richmond Since 1913", "Commonwealth of Virginia"]}
        />
      </div>
    </section>
  );
};

const Home = () => {
  usePageMeta(
    "Bass Crane Service, LLC | Crane & Truck Rental in Richmond, VA",
    "Crane and truck rental, rigging, and hauling in Richmond, VA since 1913. CCO certified operators, cranes from 30 to 160 ton. Call 24/7: 804-233-0113."
  );

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Intro */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-14 md:py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="overline text-[#1C3172] mb-6">[ Since {COMPANY.since} ]</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl uppercase text-[#1A1A1A] leading-[0.95]">
                <MaskedLinesInView lines={["Three", "Generations of", "Lifting Richmond", "Higher"]} lineClassName="inline" />
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
            <Reveal delay={0.15}>
              <p className="text-[#2B2F38] text-lg md:text-xl leading-relaxed">
                Bass Crane Service has been providing crane and truck rental services to Richmond and
                the surrounding areas for 3 generations. Our business has been in and around
                construction since 1913.
              </p>
              <p className="mt-6 text-[#5B6270] text-base leading-relaxed">
                Whatever your project requires, our experienced operators and knowledgeable staff will
                make sure your job is done correctly and safely.
              </p>
              <Link to="/about" data-testid="intro-learn-more" className="group mt-8 inline-flex items-center gap-3 font-mono-plex uppercase tracking-widest text-sm text-[#1C3172] hover:text-[#2A4FB0] transition-colors duration-300">
                Learn More
                <span className="w-8 h-px bg-current transition-all duration-300 group-hover:w-12" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="home-services">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-24 md:pt-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <h2 className="text-4xl md:text-6xl uppercase text-[#1A1A1A]">
              <MaskedLinesInView lines={["What We Lift"]} />
            </h2>
            <p className="max-w-md text-[#5B6270]">
              The right machine for every technical requirement, residential to commercial, across
              the Commonwealth of Virginia.
            </p>
          </div>
        </div>
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#E5E7EB]">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.id];
            return (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group relative flex flex-col h-full p-8 md:p-10 border-r border-b border-[#E5E7EB] bg-white transition-colors duration-500 hover:bg-[#F7F8FA]"
                >
                  <div className="flex items-center justify-between mb-16">
                    <Icon className="w-8 h-8 text-[#1C3172]" strokeWidth={1.5} />
                    <span className="font-mono-plex text-sm text-[#AEB5C4]">{s.num}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl uppercase text-[#1A1A1A] mb-4">{s.title}</h3>
                  <p className="text-[#5B6270] text-base leading-relaxed flex-1">{s.short}</p>
                  <span className="mt-8 inline-flex items-center gap-2 font-mono-plex uppercase tracking-widest text-sm text-[#5B6270] group-hover:text-[#1C3172] transition-colors duration-300">
                    Explore
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
        <div className="h-24 md:h-32" />
      </section>

      {/* Trust band */}
      <section className="bg-[#F7F8FA] border-b border-[#E5E7EB]" data-testid="home-trust">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20">
          <Reveal className="flex flex-col lg:flex-row items-start lg:items-center gap-8 mb-12">
            <ShieldCheck className="w-12 h-12 text-[#1C3172] shrink-0" strokeWidth={1.25} />
            <div>
              <p className="overline text-[#1C3172] mb-3">Certified & Safe</p>
              <h3 className="text-2xl md:text-4xl uppercase text-[#1A1A1A] max-w-3xl">
                CCO certified operators, SC&RA member, and SAM registered. Credentials that matter on every jobsite.
              </h3>
            </div>
          </Reveal>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4" aria-label="Certifications and memberships">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.06}>
                <li className="h-full bg-white border border-[#E5E7EB] p-6 md:p-8 flex items-center gap-6">
                  <img
                    src={c.logo}
                    alt={c.alt}
                    loading="lazy"
                    className="h-16 md:h-20 w-auto max-w-[45%] object-contain shrink-0"
                  />
                  <div>
                    <p className="font-display text-lg md:text-xl uppercase text-[#1A1A1A] leading-tight">{c.name}</p>
                    <p className="mt-1 text-sm text-[#5B6270] leading-snug">{c.desc}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* Projects teaser */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="home-projects-teaser">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="mb-10">
            <h2 className="text-4xl md:text-6xl uppercase text-[#1A1A1A] max-w-3xl">
              <MaskedLinesInView lines={["Proven Performance on", "Richmond's Toughest Jobs"]} />
            </h2>
            <Link to="/projects" data-testid="teaser-view-projects" className="group mt-6 inline-flex items-center gap-3 font-mono-plex uppercase tracking-widest text-sm text-[#1C3172] hover:text-[#2A4FB0] transition-colors duration-300">
              View Our Projects
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Placeholder
              src="/images/teaser-downtown.jpg"
              alt="Bass Crane boom extended beside a downtown Richmond high-rise"
              className="aspect-[4/3]"
              parallax
              testId="teaser-project-1"
            />
            <Placeholder
              src="/images/teaser-lift.jpg"
              alt="160-ton Bass Crane setting a highway sign structure at night for VDOT"
              className="aspect-[4/3]"
              parallax
              testId="teaser-project-2"
            />
          </div>
        </div>
      </section>

      <CTABand
        heading="Need a Lift?"
        sub={`Call us anytime, 24/7, to schedule a crane or truck.`}
      />
    </div>
  );
};

export default Home;
